/**
 * Streaming Slice
 *
 * Manages streaming/loading state and provides actions for sending messages,
 * stopping streams, and retrying failed messages.
 *
 * This slice contains the core message sending logic including:
 * - Deep research streaming
 * - Multimodal streaming (files + chat)
 * - Regular enhanced bot streaming
 * - Preview bot streaming
 * - Default chat streaming
 */

import type { StateCreator } from 'zustand';
import i18n from '../../../../i18n';
import { chatService } from '../../../../shared/services/ChatService';
import { ChatSessionService } from '../../../../shared/services/ChatSessionService';
import { conversationService } from '../../../../shared/services/ConversationService';
import { queryClient } from '../../../../shared/config/queryClient.config';
import { queryKeys } from '../../../../shared/config/queryKeys.config';
import { generalChatSettingsService } from '../../../settings';
import { useAuthStore } from '../../../auth/store/authStore';
import type {
  Bot,
  Model,
  WebSearchSource,
} from '../../../../shared/types/api.types';
import type {
  ChatMessage,
  FileAttachment,
} from '../../../../shared/types/chat.types';
import type { EnhancedBot } from '../../../assistant-hub/types';
import { convertFilesToMediaFiles } from '../../../../shared/utils/fileUtils';
import type { ChatState, StreamingSlice } from '../types';
import {
  buildAiPlaceholder,
  buildInitialDeepResearchContext,
} from '../utils/messageBuilderUtils';
import {
  createStreamTimeoutManager,
  clearAllStreamTimeouts,
  createStreamTimeoutHandler,
  updateMessageSources,
  createDeepResearchUpdateHandler,
} from '../utils/streamHandlerUtils';
import { currentDate } from '@/shared/utils/dateUtils';

/**
 * Initial state for the Streaming slice
 */
export const streamingInitialState: Pick<
  StreamingSlice,
  'isLoading' | 'isStreaming' | 'pendingRequests' | 'error'
> = {
  isLoading: false,
  isStreaming: false,
  pendingRequests: 0,
  error: null,
};

// Stream timeout manager for per-message timeout tracking
const streamTimeoutManager = createStreamTimeoutManager();
// Cancellation flag to stop processing stream updates
let isStreamCancelled = false;

/**
 * Creates the streaming slice with message sending and streaming actions.
 */
export const createStreamingSlice: StateCreator<
  ChatState,
  [
    ['zustand/devtools', never],
    ['zustand/persist', unknown],
    ['zustand/immer', never],
  ],
  [],
  StreamingSlice
> = (set, get) => {
  // Helper to update message sources using the utility function
  const fetchAndUpdateMessageSources = (
    conversationId: string | null,
    aiMessageId: string
  ) => {
    updateMessageSources({
      conversationId,
      aiMessageId,
      updateMessages: updater => {
        set(
          state => ({ messages: updater(state.messages) }),
          false,
          'streaming/updateSources'
        );
      },
    });
  };

  return {
    // Initial state
    ...streamingInitialState,

    // Actions
    setError: error => {
      set({ error }, false, 'streaming/setError');
    },

    setIsLoading: isLoading => {
      set({ isLoading }, false, 'streaming/setIsLoading');
    },

    setIsStreaming: isStreaming => {
      set({ isStreaming }, false, 'streaming/setIsStreaming');
    },

    stopStreaming: () => {
      // Set cancellation flag to prevent further stream updates
      isStreamCancelled = true;

      // Clear all active stream timeouts
      clearAllStreamTimeouts(streamTimeoutManager);

      // Reset streaming state
      set(
        state => ({
          isLoading: false,
          isStreaming: false,
          streamingMessage: null,
          // Mark the last AI message as stopped if it exists
          messages: state.messages.map(msg =>
            msg.author === 'ai' && msg.status === 'sending'
              ? {
                  ...msg,
                  status: 'failed',
                  error: i18n.t('chat.error.streamInterrupted'),
                }
              : msg
          ),
        }),
        false,
        'streaming/stop'
      );
    },

    retryMessage: async messageId => {
      const message = get().messages.find(m => m.id === messageId);
      if (!message || message.author !== 'user') return;

      // Simply resend the original user message
      await get().sendMessage(message.text);
    },

    sendMessage: async (text, attachments) => {
      const {
        conversationId,
        currentModel,
        currentBot,
        isWebSearchEnabled,
        isDeepResearchEnabled,
        chatSource,
      } = get();
      const { user } = useAuthStore.getState();

      // Validate prerequisites
      if (!user || !currentModel) {
        set(
          { error: i18n.t('errors.general.loginAndSelectModel') },
          false,
          'streaming/sendMessage:validationError'
        );
        return;
      }

      // Track conversation state
      const isNewConversation = !conversationId;
      const serverConversationId: string | null = null;

      // Track multimodal state
      const wasMultimodalBefore = get().hasMultimodalFilesInConversation;
      const hasAttachmentsNow = attachments && attachments.length > 0;
      const isFirstMultimodalMessage =
        hasAttachmentsNow && !wasMultimodalBefore;

      // Create user message
      const userMessageId = crypto.randomUUID();
      const userMessage: ChatMessage = {
        id: userMessageId,
        text,
        author: 'user',
        timestamp: currentDate(),
        status: 'sent',
        attachments: attachments || [],
      };

      // Create AI message placeholder
      const aiMessageId = crypto.randomUUID();
      const aiAssistantName =
        currentBot && !currentBot.is_default ? currentBot.bot_name : undefined;

      const aiMessage: ChatMessage = buildAiPlaceholder({
        aiMessageId,
        userMessageId,
        aiAssistantName,
      });

      // Initialize deep research context if enabled
      if (isDeepResearchEnabled) {
        aiMessage.deepResearchContext = buildInitialDeepResearchContext();
      }

      // Reset cancellation flag for new stream
      isStreamCancelled = false;
      streamTimeoutManager.completedFlags.set(aiMessageId, false);

      // Update state with both messages
      const hasAttachments = attachments && attachments.length > 0;
      set(
        state => ({
          messages: [...state.messages, userMessage, aiMessage],
          conversationId: isNewConversation ? null : conversationId,
          isLoading: true,
          isStreaming: true,
          error: null,
          streamingMessage: '',
          hasMultimodalFilesInConversation:
            hasAttachments || state.hasMultimodalFilesInConversation,
        }),
        false,
        'streaming/sendMessage:start'
      );

      try {
        // Keep raw files for multimodal
        let rawFiles: File[] | undefined = undefined;

        if (attachments && attachments.length > 0) {
          rawFiles = attachments.map(att => att.file);
          // Validate and convert files
          for (const file of rawFiles) {
            if (file.size === 0) {
              // File is empty - may be from page refresh
            }
          }

          if (!isWebSearchEnabled) {
            await convertFilesToMediaFiles(rawFiles);
          }
        }

        // Set up timeout handler
        const streamTimeout = 60 * 60 * 1000; // 60 minutes

        const setupStreamTimeout = createStreamTimeoutHandler({
          manager: streamTimeoutManager,
          messageId: aiMessageId,
          config: {
            timeout: streamTimeout,
            isWebSearchEnabled,
          },
          getMessages: () => get().messages,
          onTimeout: (messageId, errorMessage) => {
            set(
              state => ({
                messages: state.messages.map(msg =>
                  msg.id === messageId
                    ? { ...msg, status: 'failed', error: errorMessage }
                    : msg
                ),
                isLoading: false,
                isStreaming: false,
                streamingMessage: null,
                error: isWebSearchEnabled
                  ? errorMessage
                  : 'Stream timeout - no end event received',
              }),
              false,
              'streaming/sendMessage:timeout'
            );
          },
        });

        // Set up deep research update handler
        const handleDeepResearchUpdate = createDeepResearchUpdateHandler({
          messageId: aiMessageId,
          isStreamCancelled: () => isStreamCancelled,
          updateMessages: updater => {
            set(
              state => ({ messages: updater(state.messages) }),
              false,
              'streaming/sendMessage:deepResearchUpdate'
            );
          },
        });

        // Start the timeout
        setupStreamTimeout();

        // Route to appropriate streaming handler
        if (isDeepResearchEnabled && currentBot) {
          await handleDeepResearchStream({
            text,
            conversationId,
            currentBot,
            currentModel,
            user,
            aiMessageId,
            isNewConversation,
            isFirstMultimodalMessage,
            setupStreamTimeout,
            handleDeepResearchUpdate,
            fetchAndUpdateMessageSources,
            get,
            set,
            streamTimeoutManager,
            serverConversationId,
          });
        } else if (hasEnhancedSession(currentBot)) {
          await handleEnhancedSessionStream({
            text,
            attachments,
            conversationId,
            currentBot: currentBot!,
            currentModel,
            aiMessageId,
            userMessageId,
            isNewConversation,
            isFirstMultimodalMessage,
            isWebSearchEnabled,
            chatSource,
            setupStreamTimeout,
            handleDeepResearchUpdate,
            fetchAndUpdateMessageSources,
            get,
            set,
            streamTimeoutManager,
          });
        } else if (currentBot) {
          await handlePreviewBotStream({
            text,
            conversationId,
            currentBot,
            currentModel,
            user,
            aiMessageId,
            isNewConversation,
            isWebSearchEnabled,
            chatSource,
            rawFiles,
            setupStreamTimeout,
            get,
            set,
            streamTimeoutManager,
          });
        } else {
          await handleDefaultChatStream({
            text,
            conversationId,
            currentModel,
            user,
            aiMessageId,
            isNewConversation,
            isWebSearchEnabled,
            chatSource,
            rawFiles,
            setupStreamTimeout,
            get,
            set,
            streamTimeoutManager,
          });
        }
      } catch (error) {
        // Clear any existing stream timeout
        const timeout = streamTimeoutManager.timeouts.get(aiMessageId);
        if (timeout) {
          clearTimeout(timeout);
          streamTimeoutManager.timeouts.delete(aiMessageId);
        }
        streamTimeoutManager.completedFlags.delete(aiMessageId);

        const errorMessage =
          error instanceof Error
            ? error.message
            : i18n.t('errors.chat.sendMessageFailed');

        set(
          state => ({
            messages: state.messages.map(msg =>
              msg.id === aiMessageId
                ? { ...msg, status: 'failed', error: errorMessage }
                : msg
            ),
            isLoading: false,
            isStreaming: false,
            streamingMessage: null,
            error: errorMessage,
          }),
          false,
          'streaming/sendMessage:unexpectedError'
        );
      }
    },
  };
};

/**
 * Checks if the bot has an enhanced session
 */
function hasEnhancedSession(bot: Bot | null): boolean {
  if (!bot) return false;
  return Boolean(
    bot.sessionId &&
      bot.chatEndpoint &&
      !bot.sessionId.startsWith('fallback_') &&
      ((bot as Bot & EnhancedBot)?.enhancedBotDetails || bot.datastore)
  );
}

// ============================================================================
// Stream Handler Functions
// ============================================================================

interface BaseStreamParams {
  text: string;
  conversationId: string | null;
  currentModel: Model;
  user: { email?: string; uid?: string };
  aiMessageId: string;
  isNewConversation: boolean;
  setupStreamTimeout: () => void;
  get: () => ChatState;
  set: (
    partial: Partial<ChatState> | ((state: ChatState) => Partial<ChatState>),
    replace?: boolean,
    action?: string
  ) => void;
  streamTimeoutManager: ReturnType<typeof createStreamTimeoutManager>;
}

interface DeepResearchStreamParams extends BaseStreamParams {
  currentBot: Bot;
  isFirstMultimodalMessage: boolean | undefined;
  handleDeepResearchUpdate: ReturnType<typeof createDeepResearchUpdateHandler>;
  fetchAndUpdateMessageSources: (
    conversationId: string | null,
    aiMessageId: string
  ) => void;
  serverConversationId: string | null;
}

async function handleDeepResearchStream(
  params: DeepResearchStreamParams
): Promise<void> {
  const {
    text,
    conversationId,
    currentBot,
    currentModel,
    user,
    aiMessageId,
    isNewConversation,
    isFirstMultimodalMessage,
    setupStreamTimeout,
    handleDeepResearchUpdate,
    fetchAndUpdateMessageSources,
    get,
    set,
    streamTimeoutManager,
  } = params;
  let serverConversationId = params.serverConversationId;

  try {
    // Use a fresh UUID for new conversations to prevent collisions across
    // duplicated browser tabs that share the same persisted sessionId.
    const sessionIdToUse = conversationId || crypto.randomUUID();

    const deepResearchConfig: Record<string, unknown> = {};
    if (currentModel) {
      if (currentModel.modelName) {
        deepResearchConfig['planner_model'] = currentModel.modelName;
        deepResearchConfig['writer_model'] = currentModel.modelName;
      }
      if (currentModel.modelProvider) {
        deepResearchConfig['planner_provider'] = currentModel.modelProvider;
      }
    }

    const response = await ChatSessionService.sendDeepResearchMessage(
      text,
      sessionIdToUse,
      currentBot._id,
      deepResearchConfig,
      conversationId || undefined,
      currentBot._id,
      user?.email || user?.uid || undefined
    );

    let accumulatedText = '';

    await ChatSessionService.handleStreamingResponse(
      response,
      (chunk: string) => {
        if (isStreamCancelled) return;

        accumulatedText += chunk;

        const timeout = streamTimeoutManager.timeouts.get(aiMessageId);
        if (timeout) clearTimeout(timeout);
        setupStreamTimeout();

        set(
          state => ({
            streamingMessage: accumulatedText,
            messages: state.messages.map(msg => {
              if (msg.id !== aiMessageId) return msg;

              const shouldKeepSentStatus =
                msg.status === 'sent' ||
                msg.deepResearchContext?.state === 'COMPLETED_COLLAPSED' ||
                msg.deepResearchContext?.state === 'COMPLETED_EXPANDED';

              return {
                ...msg,
                text: accumulatedText,
                status: shouldKeepSentStatus ? 'sent' : ('sending' as const),
                toolSteps: undefined,
              };
            }),
          }),
          false,
          'streaming/deepResearch:streamUpdate'
        );
      },
      (receivedConversationId?: string) => {
        if (isStreamCancelled) return;

        streamTimeoutManager.completedFlags.set(aiMessageId, true);

        const timeout = streamTimeoutManager.timeouts.get(aiMessageId);
        if (timeout) {
          clearTimeout(timeout);
          streamTimeoutManager.timeouts.delete(aiMessageId);
        }

        if (isNewConversation && receivedConversationId) {
          serverConversationId = receivedConversationId;
        }

        const isEmptyStream =
          !accumulatedText || accumulatedText.trim().length === 0;

        set(
          state => ({
            messages: state.messages.map(msg => {
              if (msg.id !== aiMessageId) return msg;

              const updatedDeepResearchContext = msg.deepResearchContext
                ? {
                    ...msg.deepResearchContext,
                    state:
                      !isEmptyStream &&
                      (msg.deepResearchContext.state === 'ACTIVE' ||
                        msg.deepResearchContext.state === 'IDLE')
                        ? ('COMPLETED_COLLAPSED' as const)
                        : msg.deepResearchContext.state,
                  }
                : undefined;

              return {
                ...msg,
                text: accumulatedText,
                status: isEmptyStream ? 'failed' : 'sent',
                error: isEmptyStream ? 'Empty response received' : undefined,
                deepResearchContext: updatedDeepResearchContext,
              };
            }),
            isLoading: false,
            isStreaming: false,
            streamingMessage: null,
            ...(serverConversationId && {
              conversationId: serverConversationId,
            }),
          }),
          false,
          'streaming/deepResearch:complete'
        );

        // Lock model for first multimodal message
        const finalConversationId = serverConversationId || conversationId;
        if (
          isFirstMultimodalMessage &&
          finalConversationId &&
          currentModel &&
          !isEmptyStream
        ) {
          conversationService
            .lockModel(finalConversationId, Number(currentModel.id))
            .catch(error => {
              console.error('[ChatStore] Failed to lock model:', error);
            });
        }

        // Fetch sources after streaming completes
        if (finalConversationId && !isEmptyStream) {
          fetchAndUpdateMessageSources(finalConversationId, aiMessageId);
        }

        // Update sidebar
        const state = get();
        if (state.newChatTrigger || isNewConversation) {
          get().fetchConversations();
        }

        if (state.newChatTrigger) {
          set(
            prevState => ({ ...prevState, newChatTrigger: false }),
            false,
            'streaming/deepResearch:resetTrigger'
          );
        }
      },
      (error: Error) => {
        console.error('Deep research error:', error);
        set(
          state => ({
            messages: state.messages.map(msg =>
              msg.id === aiMessageId
                ? {
                    ...msg,
                    status: 'failed',
                    error: error.message,
                    deepResearchContext: msg.deepResearchContext
                      ? { ...msg.deepResearchContext, state: 'ERROR' }
                      : undefined,
                  }
                : msg
            ),
            isLoading: false,
            isStreaming: false,
            streamingMessage: null,
            error: error.message,
          }),
          false,
          'streaming/deepResearch:error'
        );
      },
      (convId: string) => {
        if (isNewConversation && convId) {
          serverConversationId = convId;
          set(
            () => ({ conversationId: convId }),
            false,
            'streaming/deepResearch:setConversationId'
          );
        }
      },
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      handleDeepResearchUpdate as (update: unknown) => void
    );
  } catch (error) {
    console.error('Deep research request failed:', error);
    set(
      state => ({
        messages: state.messages.map(msg =>
          msg.id === aiMessageId
            ? {
                ...msg,
                status: 'failed',
                error: 'Deep research failed',
                deepResearchContext: msg.deepResearchContext
                  ? { ...msg.deepResearchContext, state: 'ERROR' }
                  : undefined,
              }
            : msg
        ),
        isLoading: false,
        isStreaming: false,
        streamingMessage: null,
        error: error instanceof Error ? error.message : 'Deep research failed',
      }),
      false,
      'streaming/deepResearch:failed'
    );
  }
}

interface EnhancedSessionStreamParams extends Omit<BaseStreamParams, 'user'> {
  attachments?: FileAttachment[];
  currentBot: Bot;
  currentModel: Model;
  userMessageId: string;
  isFirstMultimodalMessage: boolean | undefined;
  isWebSearchEnabled: boolean;
  chatSource: 'sidebar' | 'assistant' | null;
  handleDeepResearchUpdate: ReturnType<typeof createDeepResearchUpdateHandler>;
  fetchAndUpdateMessageSources: (
    conversationId: string | null,
    aiMessageId: string
  ) => void;
}

async function handleEnhancedSessionStream(
  params: EnhancedSessionStreamParams
): Promise<void> {
  const {
    text,
    attachments,
    conversationId,
    currentBot,
    currentModel,
    // user is available via params if needed
    aiMessageId,
    isNewConversation,
    isFirstMultimodalMessage,
    isWebSearchEnabled,
    chatSource,
    // rawFiles is not directly used - files are extracted from attachments
    setupStreamTimeout,
    handleDeepResearchUpdate,
    fetchAndUpdateMessageSources,
    get,
    set,
    streamTimeoutManager,
  } = params;

  // Check for GCS URLs from previous upload
  const files = attachments?.map(att => att.file) || [];
  const hasFiles = files.length > 0;
  const gcsFileUrls: string[] = [];
  let useGcsUrls = false;

  if (hasFiles && attachments) {
    const allFilesEmpty = files.every(f => f.size === 0);
    const allHaveUrls = attachments.every(
      att => att.url && att.url.startsWith('https://')
    );

    if (allFilesEmpty && allHaveUrls) {
      useGcsUrls = true;
      attachments.forEach(att => {
        if (att.url) gcsFileUrls.push(att.url);
      });
    }
  }

  const enhancedBot = currentBot as Bot & EnhancedBot;
  const enhancedBotDetails = enhancedBot.enhancedBotDetails || currentBot;
  // Use a fresh UUID for new conversations to prevent collisions across
  // duplicated browser tabs that share the same persisted sessionId.
  const sessionIdToUse = conversationId || crypto.randomUUID();

  let accumulatedText = '';
  let serverConversationId: string | null = null;

  // Common stream callbacks
  const onChunk = (chunk: string) => {
    if (isStreamCancelled) return;

    accumulatedText += chunk;

    const timeout = streamTimeoutManager.timeouts.get(aiMessageId);
    if (timeout) clearTimeout(timeout);
    setupStreamTimeout();

    set(
      state => ({
        streamingMessage: accumulatedText,
        messages: state.messages.map(msg =>
          msg.id === aiMessageId
            ? { ...msg, text: accumulatedText, status: 'sending' as const }
            : msg
        ),
      }),
      false,
      'streaming/enhanced:streamUpdate'
    );
  };

  const onComplete = (receivedConversationId?: string) => {
    if (isStreamCancelled) return;

    streamTimeoutManager.completedFlags.set(aiMessageId, true);

    const timeout = streamTimeoutManager.timeouts.get(aiMessageId);
    if (timeout) {
      clearTimeout(timeout);
      streamTimeoutManager.timeouts.delete(aiMessageId);
    }

    if (isNewConversation && receivedConversationId) {
      serverConversationId = receivedConversationId;
    }

    const isEmptyStream =
      !accumulatedText || accumulatedText.trim().length === 0;

    if (isEmptyStream) {
      const webSearchNoResultsMsg =
        'No web results found. Try a different query.';
      set(
        state => ({
          messages: state.messages.map(msg =>
            msg.id === aiMessageId
              ? {
                  ...msg,
                  text: '',
                  status: 'failed' as const,
                  error: isWebSearchEnabled
                    ? webSearchNoResultsMsg
                    : i18n.t('chat.error.newChatBanner'),
                }
              : msg
          ),
          isLoading: false,
          isStreaming: false,
          streamingMessage: null,
          error: isWebSearchEnabled
            ? webSearchNoResultsMsg
            : 'Empty response received',
        }),
        false,
        'streaming/enhanced:emptyStream'
      );
    } else {
      set(
        state => ({
          messages: state.messages.map(msg =>
            msg.id === aiMessageId
              ? { ...msg, text: accumulatedText, status: 'sent' as const }
              : msg
          ),
          isLoading: false,
          isStreaming: false,
          streamingMessage: null,
          ...(serverConversationId && {
            conversationId: serverConversationId,
          }),
        }),
        false,
        'streaming/enhanced:complete'
      );

      // Lock model for first multimodal message
      const finalConversationId = serverConversationId || conversationId;
      if (isFirstMultimodalMessage && finalConversationId && currentModel) {
        conversationService
          .lockModel(finalConversationId, Number(currentModel.id))
          .catch(error => {
            console.error('[ChatStore] Failed to lock model:', error);
          });
      }

      // Fetch sources
      if (finalConversationId) {
        fetchAndUpdateMessageSources(finalConversationId, aiMessageId);
      }
    }

    // Update sidebar
    const stateForSidebar = get();
    if (stateForSidebar.newChatTrigger || isNewConversation) {
      get().fetchConversations();
    }

    if (currentBot && !currentBot.is_default) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.recentAssistants.all(),
      });
    }

    if (stateForSidebar.newChatTrigger) {
      set(
        prevState => ({ ...prevState, newChatTrigger: false }),
        false,
        'streaming/enhanced:resetTrigger'
      );
    }
  };

  const onError = (error: Error) => {
    if (isStreamCancelled) return;

    streamTimeoutManager.completedFlags.set(aiMessageId, true);

    const timeout = streamTimeoutManager.timeouts.get(aiMessageId);
    if (timeout) {
      clearTimeout(timeout);
      streamTimeoutManager.timeouts.delete(aiMessageId);
    }

    let specificError = error.message;
    if (
      error.message.includes('500') ||
      error.message.includes('Internal Server Error')
    ) {
      specificError = isWebSearchEnabled
        ? 'No web results found. Try a different query.'
        : 'Stream interrupted due to server error. Please try again.';
    }
    if (
      error.message.includes('network') ||
      error.message.includes('NetworkError') ||
      error.message.includes('fetch') ||
      error.message.includes('timeout')
    ) {
      specificError =
        'Stream interrupted due to network issue. Please try again.';
    }

    set(
      state => ({
        messages: state.messages.map(msg =>
          msg.id === aiMessageId
            ? { ...msg, status: 'failed' as const, error: specificError }
            : msg
        ),
        isLoading: false,
        isStreaming: false,
        streamingMessage: null,
        error: specificError,
      }),
      false,
      'streaming/enhanced:error'
    );
  };

  const onSessionStart = (convId: string) => {
    if (isNewConversation && convId) {
      serverConversationId = convId;
      set(
        () => ({ conversationId: convId, currentBot }),
        false,
        'streaming/enhanced:setConversationId'
      );
      if (currentBot._id && !currentBot.is_default) {
        const assistantId = parseInt(currentBot._id);
        if (!isNaN(assistantId)) {
          conversationService
            .createConversationAssociation(convId, assistantId)
            .catch(() => {
              // Non-critical
            });
        }
      }
    } else if (
      !isNewConversation &&
      convId &&
      currentBot._id &&
      !currentBot.is_default
    ) {
      conversationService.updateConversation(convId).catch(() => {
        // Non-critical
      });
    }
  };

  const onWebSearchMetadata = (sources: unknown[]) => {
    if (isStreamCancelled) return;
    const webSearchSources = sources as WebSearchSource[];

    set(
      state => ({
        messages: state.messages.map(msg =>
          msg.id === aiMessageId
            ? {
                ...msg,
                webSearchSources,
                additional_kwargs: {
                  ...msg.additional_kwargs,
                  web_search_sources: webSearchSources,
                  web_results: webSearchSources,
                },
              }
            : msg
        ),
      }),
      false,
      'streaming/enhanced:webSearchMetadata'
    );
  };

  const onRagMetadata = (sources: unknown[]) => {
    if (isStreamCancelled) return;
    type RagSourceType = NonNullable<
      import('../../../../shared/types/api.types').MessageAdditionalKwargs['rag_sources']
    >[number];
    const ragSources = sources as RagSourceType[];

    set(
      state => ({
        messages: state.messages.map(msg =>
          msg.id === aiMessageId
            ? {
                ...msg,
                ragSources,
                additional_kwargs: {
                  ...msg.additional_kwargs,
                  rag_sources: ragSources,
                },
              }
            : msg
        ),
      }),
      false,
      'streaming/enhanced:ragMetadata'
    );
  };

  // Use multimodal or regular streaming
  if (hasFiles || useGcsUrls) {
    await ChatSessionService.sendMultimodalMessageStream(
      text,
      sessionIdToUse,
      currentBot._id,
      useGcsUrls ? [] : files,
      enhancedBotDetails,
      isWebSearchEnabled,
      currentModel,
      {
        onChunk,
        onDeepResearchUpdate: handleDeepResearchUpdate as (
          update: unknown
        ) => void,
        onComplete,
        onError,
        onSessionStart,
        onWebSearchMetadata,
        onRagMetadata,
      },
      chatSource,
      useGcsUrls ? gcsFileUrls : undefined
    );
  } else {
    // Regular text-only streaming
    const dynamicToolMessages: string[] = [];
    let hasReceivedToken = false;

    const response = await ChatSessionService.sendMessage(
      text,
      sessionIdToUse,
      currentBot.chatEndpoint!,
      currentBot._id,
      files,
      enhancedBotDetails,
      isWebSearchEnabled,
      currentModel,
      chatSource
    );

    await ChatSessionService.handleStreamingResponse(
      response,
      (chunk: string) => {
        if (isStreamCancelled) return;
        hasReceivedToken = true;
        onChunk(chunk);
        // Clear tool steps when tokens start
        set(
          state => ({
            messages: state.messages.map(msg =>
              msg.id === aiMessageId ? { ...msg, toolSteps: undefined } : msg
            ),
          }),
          false,
          'streaming/enhanced:clearToolSteps'
        );
      },
      (receivedConversationId?: string) => {
        if (isStreamCancelled) return;

        streamTimeoutManager.completedFlags.set(aiMessageId, true);

        const timeout = streamTimeoutManager.timeouts.get(aiMessageId);
        if (timeout) {
          clearTimeout(timeout);
          streamTimeoutManager.timeouts.delete(aiMessageId);
        }

        if (isNewConversation && receivedConversationId) {
          serverConversationId = receivedConversationId;
        }

        const hasText = accumulatedText && accumulatedText.trim().length > 0;

        if (hasText) {
          set(
            state => ({
              messages: state.messages.map(msg =>
                msg.id === aiMessageId
                  ? { ...msg, text: accumulatedText, status: 'sent' as const }
                  : msg
              ),
              isStreaming: false,
              isLoading: false,
              streamingMessage: null,
              ...(serverConversationId && {
                conversationId: serverConversationId,
              }),
            }),
            false,
            'streaming/enhanced:completeImmediate'
          );

          const finalConversationId = serverConversationId || conversationId;
          if (finalConversationId) {
            fetchAndUpdateMessageSources(finalConversationId, aiMessageId);
          }
        }

        // Update sidebar
        const stateForSidebar = get();
        if (stateForSidebar.newChatTrigger || isNewConversation) {
          get().fetchConversations();
        }

        if (currentBot && !currentBot.is_default) {
          void queryClient.invalidateQueries({
            queryKey: queryKeys.recentAssistants.all(),
          });
        }

        // Grace period for pending tokens
        const GRACE_PERIOD_MS = 500 * 1000;
        setTimeout(() => {
          const isEmptyStream =
            !accumulatedText || accumulatedText.trim().length === 0;

          if (isEmptyStream && !hasText) {
            const webSearchNoResultsMsg =
              'No web results found. Try a different query.';
            set(
              state => ({
                messages: state.messages.map(msg =>
                  msg.id === aiMessageId
                    ? {
                        ...msg,
                        text: '',
                        status: 'failed' as const,
                        error: isWebSearchEnabled
                          ? webSearchNoResultsMsg
                          : i18n.t('chat.error.newChatBanner'),
                      }
                    : msg
                ),
                isLoading: false,
                isStreaming: false,
                streamingMessage: null,
                error: isWebSearchEnabled
                  ? webSearchNoResultsMsg
                  : 'Empty response received',
              }),
              false,
              'streaming/enhanced:emptyStreamGrace'
            );
          } else if (hasText) {
            set(
              state => ({
                messages: state.messages.map(msg =>
                  msg.id === aiMessageId
                    ? {
                        ...msg,
                        text: accumulatedText,
                        status: 'sent' as const,
                      }
                    : msg
                ),
                isLoading: false,
                isStreaming: false,
                streamingMessage: null,
              }),
              false,
              'streaming/enhanced:completeFinalize'
            );
          }

          const stateForTrigger = get();
          if (stateForTrigger.newChatTrigger) {
            set(
              prevState => ({ ...prevState, newChatTrigger: false }),
              false,
              'streaming/enhanced:resetTrigger'
            );
          }
        }, GRACE_PERIOD_MS);
      },
      onError,
      onSessionStart,
      (_tool: string, content: string) => {
        if (isStreamCancelled || hasReceivedToken) return;
        dynamicToolMessages.push(content);
        set(
          state => ({
            messages: state.messages.map(msg =>
              msg.id === aiMessageId
                ? {
                    ...msg,
                    toolSteps: {
                      items: [...dynamicToolMessages],
                      interval: 2000,
                    },
                  }
                : msg
            ),
          }),
          false,
          'streaming/enhanced:toolStart'
        );
      },
      (tool: string, content: string) => {
        if (isStreamCancelled || hasReceivedToken) return;
        const lastIndex = dynamicToolMessages.length - 1;
        if (lastIndex >= 0 && dynamicToolMessages[lastIndex].includes(tool)) {
          dynamicToolMessages[lastIndex] = content;
        } else {
          dynamicToolMessages.push(content);
        }
        set(
          state => ({
            messages: state.messages.map(msg =>
              msg.id === aiMessageId
                ? {
                    ...msg,
                    toolSteps: {
                      items: [...dynamicToolMessages],
                      interval: 2000,
                    },
                  }
                : msg
            ),
          }),
          false,
          'streaming/enhanced:toolEnd'
        );
      },
      (content: string) => {
        if (isStreamCancelled || hasReceivedToken) return;
        if (content && content.trim()) {
          const cleanContent = content.trim().slice(0, 100);
          if (cleanContent && !dynamicToolMessages.includes(cleanContent)) {
            dynamicToolMessages.push(cleanContent);
            set(
              state => ({
                messages: state.messages.map(msg =>
                  msg.id === aiMessageId
                    ? {
                        ...msg,
                        toolSteps: {
                          items: [...dynamicToolMessages],
                          interval: 2000,
                        },
                      }
                    : msg
                ),
              }),
              false,
              'streaming/enhanced:thinking'
            );
          }
        }
      },
      onWebSearchMetadata,
      onRagMetadata,
      handleDeepResearchUpdate as (update: unknown) => void
    );
  }
}

interface PreviewBotStreamParams extends Omit<BaseStreamParams, 'user'> {
  currentBot: Bot;
  isWebSearchEnabled: boolean;
  chatSource: 'sidebar' | 'assistant' | null;
  rawFiles?: File[];
  user: { email?: string };
}

async function handlePreviewBotStream(
  params: PreviewBotStreamParams
): Promise<void> {
  const {
    text,
    conversationId,
    currentBot,
    currentModel,
    user,
    aiMessageId,
    isNewConversation,
    isWebSearchEnabled,
    chatSource,
    rawFiles,
    setupStreamTimeout,
    get,
    set,
    streamTimeoutManager,
  } = params;

  const botWithModel = currentBot as Bot & {
    default_model_detail?: Model | null;
  };
  const previewModel = botWithModel.default_model_detail ?? currentModel;

  const temperature = currentBot.temperature || 0.5;
  const systemPrompt =
    currentBot.system_instructions || 'I am a helpful assistant.';
  const kValue = currentBot.k_value || 5;

  let accumulatedText = '';
  let currentConversationId = conversationId;

  if (isNewConversation) {
    currentConversationId = crypto.randomUUID();
    set(
      () => ({ conversationId: currentConversationId }),
      false,
      'streaming/previewBot:setNewConversationId'
    );
  }

  await chatService.sendMessageWithStreaming(
    text,
    currentConversationId!,
    user.email || '',
    {
      modelName: previewModel.modelName,
      provider: previewModel.modelProvider,
      systemPrompt,
      temperature,
      k: kValue,
      botId: currentBot._id,
      dataStores: currentBot.datastore || [],
      isDefaultBot: currentBot.is_default,
    },
    {
      onStreamUpdate: (chunk: string) => {
        if (isStreamCancelled) return;
        accumulatedText += chunk;

        const timeout = streamTimeoutManager.timeouts.get(aiMessageId);
        if (timeout) clearTimeout(timeout);
        setupStreamTimeout();

        set(
          state => ({
            streamingMessage: accumulatedText,
            messages: state.messages.map(msg =>
              msg.id === aiMessageId
                ? { ...msg, text: accumulatedText, status: 'sending' as const }
                : msg
            ),
          }),
          false,
          'streaming/previewBot:streamUpdate'
        );
      },
      onComplete: (finalText: string) => {
        if (isStreamCancelled) return;

        streamTimeoutManager.completedFlags.set(aiMessageId, true);

        const timeout = streamTimeoutManager.timeouts.get(aiMessageId);
        if (timeout) {
          clearTimeout(timeout);
          streamTimeoutManager.timeouts.delete(aiMessageId);
        }

        if (!finalText || finalText.trim() === '') {
          set(
            state => ({
              messages: state.messages.map(msg =>
                msg.id === aiMessageId
                  ? {
                      ...msg,
                      text: i18n.t('chat.emptyResponse'),
                      status: 'sent' as const,
                    }
                  : msg
              ),
              isLoading: false,
              isStreaming: false,
              streamingMessage: null,
            }),
            false,
            'streaming/previewBot:emptyStream'
          );
        } else {
          set(
            state => ({
              messages: state.messages.map(msg =>
                msg.id === aiMessageId
                  ? { ...msg, text: finalText, status: 'sent' as const }
                  : msg
              ),
              isLoading: false,
              isStreaming: false,
              streamingMessage: null,
            }),
            false,
            'streaming/previewBot:complete'
          );

          if (isNewConversation) {
            get().fetchConversations();
          }
        }
      },
      onError: (error: Error) => {
        if (isStreamCancelled) return;

        streamTimeoutManager.completedFlags.set(aiMessageId, true);

        const timeout = streamTimeoutManager.timeouts.get(aiMessageId);
        if (timeout) {
          clearTimeout(timeout);
          streamTimeoutManager.timeouts.delete(aiMessageId);
        }

        console.error('[ChatStore] Preview bot chat error:', error);

        set(
          state => ({
            messages: state.messages.map(msg =>
              msg.id === aiMessageId
                ? { ...msg, status: 'failed' as const, error: error.message }
                : msg
            ),
            isLoading: false,
            isStreaming: false,
            streamingMessage: null,
            error: error.message,
          }),
          false,
          'streaming/previewBot:error'
        );
      },
    },
    undefined,
    isWebSearchEnabled,
    rawFiles,
    chatSource
  );
}

interface DefaultChatStreamParams extends BaseStreamParams {
  isWebSearchEnabled: boolean;
  chatSource: 'sidebar' | 'assistant' | null;
  rawFiles?: File[];
}

async function handleDefaultChatStream(
  params: DefaultChatStreamParams
): Promise<void> {
  const {
    text,
    conversationId,
    currentModel,
    user,
    aiMessageId,
    isNewConversation,
    isWebSearchEnabled,
    chatSource,
    rawFiles,
    setupStreamTimeout,
    get,
    set,
    streamTimeoutManager,
  } = params;

  const userSettings = await generalChatSettingsService.getSettings();

  const temperature = userSettings.temperature;
  const systemPrompt =
    userSettings.systemInstructions || 'You are a helpful assistant';
  const kValue = userSettings.kValue;

  let accumulatedText = '';
  let currentConversationId = conversationId;

  if (isNewConversation) {
    currentConversationId = crypto.randomUUID();
    set(
      () => ({ conversationId: currentConversationId }),
      false,
      'streaming/default:setNewConversationId'
    );
  }

  await chatService.sendMessageWithStreaming(
    text,
    currentConversationId!,
    user.email || '',
    {
      modelName: currentModel.modelName,
      provider: currentModel.modelProvider,
      systemPrompt,
      temperature,
      k: kValue,
      isDefaultBot: true,
    },
    {
      onStreamUpdate: (chunk: string) => {
        if (isStreamCancelled) return;
        accumulatedText += chunk;

        const timeout = streamTimeoutManager.timeouts.get(aiMessageId);
        if (timeout) clearTimeout(timeout);
        setupStreamTimeout();

        set(
          state => ({
            streamingMessage: accumulatedText,
            messages: state.messages.map(msg =>
              msg.id === aiMessageId
                ? { ...msg, text: accumulatedText, status: 'sending' as const }
                : msg
            ),
          }),
          false,
          'streaming/default:streamUpdate'
        );
      },
      onComplete: (finalText: string) => {
        if (isStreamCancelled) return;

        streamTimeoutManager.completedFlags.set(aiMessageId, true);

        const timeout = streamTimeoutManager.timeouts.get(aiMessageId);
        if (timeout) {
          clearTimeout(timeout);
          streamTimeoutManager.timeouts.delete(aiMessageId);
        }

        if (!finalText || finalText.trim() === '') {
          set(
            state => ({
              messages: state.messages.map(msg =>
                msg.id === aiMessageId
                  ? {
                      ...msg,
                      text: i18n.t('chat.emptyResponse'),
                      status: 'sent' as const,
                    }
                  : msg
              ),
              isLoading: false,
              isStreaming: false,
              streamingMessage: null,
            }),
            false,
            'streaming/default:emptyStream'
          );
        } else {
          set(
            state => ({
              messages: state.messages.map(msg =>
                msg.id === aiMessageId
                  ? { ...msg, text: finalText, status: 'sent' as const }
                  : msg
              ),
              isLoading: false,
              isStreaming: false,
              streamingMessage: null,
            }),
            false,
            'streaming/default:complete'
          );

          if (isNewConversation) {
            get().fetchConversations();
          }
        }
      },
      onError: (error: Error) => {
        if (isStreamCancelled) return;

        streamTimeoutManager.completedFlags.set(aiMessageId, true);

        const timeout = streamTimeoutManager.timeouts.get(aiMessageId);
        if (timeout) {
          clearTimeout(timeout);
          streamTimeoutManager.timeouts.delete(aiMessageId);
        }

        console.error('[ChatStore] Default chat error:', error);

        set(
          state => ({
            messages: state.messages.map(msg =>
              msg.id === aiMessageId
                ? { ...msg, status: 'failed' as const, error: error.message }
                : msg
            ),
            isLoading: false,
            isStreaming: false,
            streamingMessage: null,
            error: error.message,
          }),
          false,
          'streaming/default:error'
        );
      },
    },
    undefined,
    isWebSearchEnabled,
    rawFiles,
    chatSource
  );
}
